import type { ActivityEnum } from '@/api/model'

// TR-1000 R12: only the coarse activity bucket is ever shown — never an exact timestamp.
export const activityCopy: Record<ActivityEnum, string> = {
  today: 'dziś',
  this_week: 'w tym tygodniu',
}

export function warningMessage(activity: ActivityEnum): string {
  return `To konto było używane ${activityCopy[activity]}. Reset hasła odbierze obecnemu użytkownikowi dostęp do konta. Upewnij się, że uczeń stojący przed Tobą to faktyczny właściciel konta.`
}

// Error-status → Polish copy (contract: 404 anti-enumeration, 409 not-active, 410 gone).
export const resetErrorCopy = {
  notFound: 'Podany login nie odpowiada żadnemu uczniowi w tej klasie.',
  disabled: 'Konto jest wyłączone. Aby zresetować hasło, najpierw przywróć konto.',
  blocked: 'Konto jest zablokowane. Aby zresetować hasło, najpierw odblokuj konto.',
  notActive: 'Nie można zresetować hasła — konto nie jest aktywne lub klasa jest zarchiwizowana.',
  gone: 'To konto już nie istnieje.',
  technical: 'Nie udało się zresetować hasła. Spróbuj ponownie.',
  popupLost:
    'Hasło zostało zresetowane, ale nie udało się go wyświetlić. Wykonaj ponowny reset — nowe hasło tymczasowe zastąpi poprzednie.',
} as const

export const overrideReasons = [
  { value: 'recovery_not_configured', label: 'Uczeń nie ustawił pytania bezpieczeństwa' },
  { value: 'recovery_answer_forgotten', label: 'Uczeń nie pamięta odpowiedzi' },
  { value: 'recovery_locked', label: 'Pytanie zablokowane po nieudanych próbach' },
] as const

export const observedTraceCopy = {
  today: 'dziś',
  this_week: 'w tym tygodniu',
  none: 'brak',
} as const

export const traceExplanations = [
  {
    term: 'Ostatni ślad aktywności',
    text: 'najświeższy z trzech: ostatnie logowanie ucznia, ostatnie odświeżenie rejestracji jego telefonu i ostatni zapis danych z aplikacji. „Dziś” to ostatnie 24 godziny, „w tym tygodniu” do 7 dni, „brak” oznacza, że przez 7 dni nic nie zaobserwowano. Brak nie dowodzi, że konto nie jest używane.',
  },
  {
    term: 'Aktywna sesja',
    text: 'uczeń jest zalogowany: jego logowanie nie wygasło i nie zostało zakończone. Sesja może trwać dłużej niż 7 dni, więc „brak” śladu przy aktywnej sesji jest możliwy.',
  },
  {
    term: 'Przypisane urządzenie',
    text: 'telefon ucznia, który zgłosił się do aplikacji po powiadomienia. Reset usuwa to przypisanie, a telefon zgłosi się ponownie po zalogowaniu.',
  },
] as const

const overrideConflictCopy: Record<string, string> = {
  not_active: resetErrorCopy.notActive,
  class_not_operational: 'Klasa lub szkoła ucznia jest zarchiwizowana. Reset jest niedostępny.',
  stale_preview:
    'Stan konta zmienił się od podglądu. Podgląd odświeżono — sprawdź go i potwierdź ponownie.',
  recent_activity_ack_required:
    'Konto ma ślady użycia. Podgląd odświeżono — zaznacz potwierdzenie i spróbuj ponownie.',
}

/** Kody 409, po których stan konta mógł się zmienić i podgląd trzeba pobrać od nowa. */
export const overrideRefreshCodes = ['stale_preview', 'recent_activity_ack_required']

function asRecord(data: unknown): Record<string, unknown> {
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
}

export function overrideConflictCode(data: unknown): string | null {
  const code = asRecord(data).code
  return typeof code === 'string' ? code : null
}

export function overrideErrorMessage(status: number, data: unknown): string {
  const body = asRecord(data)
  if (status === 400) {
    if (body.code === 'bad_login_confirmation') {
      return 'Wpisany login nie zgadza się z loginem ucznia.'
    }
    if ('password' in body) return 'Hasło jest nieprawidłowe.'
    if ('login_confirmation' in body) return 'Wpisz login ucznia, aby potwierdzić konto.'
    if ('reason' in body) return 'Wybierz powód resetu.'
    return 'Sprawdź wypełnione pola i spróbuj ponownie.'
  }
  if (status === 403) return 'Brak uprawnień do tej operacji.'
  if (status === 404) return resetErrorCopy.notFound
  if (status === 410) return resetErrorCopy.gone
  if (status === 429) return 'Za dużo prób. Poczekaj chwilę i spróbuj ponownie.'
  if (status === 409) {
    const code = overrideConflictCode(data)
    return (code && overrideConflictCopy[code]) || resetErrorCopy.notActive
  }
  return resetErrorCopy.technical
}
